import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Row,
  Col,
  PreviewCard,
  Button,
  RSelect,
} from "../../../components/Component";
import { Badge, Spinner } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const priorityBadge = (p) => {
  const v = (p || "").toLowerCase();
  if (v.includes("élev") || v.includes("eleve") || v.includes("high")) return "danger";
  if (v.includes("moy")) return "warning";
  return "info";
};

const PredictiveAiPage = () => {
  const [sites, setSites] = useState([]);
  const [observations, setObservations] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedObservationId, setSelectedObservationId] = useState(null);
  const [loadingObservations, setLoadingObservations] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [st, sitesRes] = await Promise.all([api.getPredictiveAiStatus(), api.getSites()]);
        setStatus(st);
        setSites(sitesRes.data || sitesRes || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchObservationsForSite = async () => {
      if (!selectedSiteId) {
        setObservations([]);
        setSelectedObservationId(null);
        return;
      }

      setLoadingObservations(true);
      setSelectedObservationId(null);
      try {
        const response = await api.getObservations({ site_id: selectedSiteId, per_page: 100 });
        const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        setObservations(rows);
      } catch (err) {
        console.error(err);
        setObservations([]);
        toast.error("Impossible de charger les observations de ce site");
      } finally {
        setLoadingObservations(false);
      }
    };

    fetchObservationsForSite();
  }, [selectedSiteId]);

  const runAnalysis = async () => {
    if (!selectedSiteId) {
      toast.warning("Veuillez choisir un site avant de lancer l'analyse IA");
      return;
    }
    if (!selectedObservationId) {
      toast.warning("Veuillez choisir une observation avant de lancer l'analyse IA");
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const body = { site_id: selectedSiteId, observation_id: selectedObservationId };
      const data = await api.postPredictiveAiInsights(body);
      setResult(data);
      toast.success("Analyse générée");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Impossible de lancer l'analyse IA";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const siteOptions = sites.map((s) => ({ value: s.id, label: s.name }));
  const observationOptions = observations.map((o) => ({
    value: o.id,
    label: `#${o.id} - ${o.title || "Observation sans titre"}`,
  }));

  return (
    <React.Fragment>
      <Head title="IA prédictive HSE" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>IA prédictive HSE</BlockTitle>
              <BlockDes>
                <p>
                  Anticipation des situations à risque et mise en évidence des zones ou thématiques dangereuses à
                  partir de vos observations et zones cartographiées (Groq).
                </p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard className="card-full">
            <div className="card-inner">
              <Row className="g-3 align-items-end mb-4">
                <Col md="4">
                  <label className="form-label">Périmètre</label>
                  <RSelect
                    options={siteOptions}
                    value={
                      selectedSiteId
                        ? {
                            value: selectedSiteId,
                            label: sites.find((s) => s.id === selectedSiteId)?.name || "Site",
                          }
                        : null
                    }
                    onChange={(opt) => setSelectedSiteId(opt?.value ?? null)}
                    placeholder="Choisir un site"
                    isClearable
                  />
                </Col>
                <Col md="4">
                  <label className="form-label">Observation</label>
                  <RSelect
                    options={observationOptions}
                    value={
                      selectedObservationId
                        ? observationOptions.find((o) => o.value === selectedObservationId) || null
                        : null
                    }
                    onChange={(opt) => setSelectedObservationId(opt?.value ?? null)}
                    placeholder={
                      !selectedSiteId
                        ? "Choisir d'abord un site"
                        : loadingObservations
                        ? "Chargement des observations..."
                        : "Choisir une observation"
                    }
                    isDisabled={!selectedSiteId || loadingObservations}
                    isLoading={loadingObservations}
                    isClearable
                  />
                  {selectedSiteId && !loadingObservations && observationOptions.length === 0 && (
                    <div className="form-note text-warning mt-1">Aucune observation disponible pour ce site.</div>
                  )}
                </Col>
                <Col md="4" className="d-grid">
                  <Button
                    color="primary"
                    className="mt-4"
                    onClick={runAnalysis}
                    disabled={loading || !selectedSiteId || !selectedObservationId || (status && status.configured === false)}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Analyse en cours…
                      </>
                    ) : (
                      <>
                        <Icon name="activity" className="me-1" />
                        Lancer l&apos;analyse IA
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

              {status && status.configured === false && (
                <div className="alert alert-warning">
                  <strong>Clé Groq manquante.</strong> Configurez{" "}
                  <code className="small">GROQ_API_KEY</code> dans vos variables d&apos;environnement (backend ou racine), puis
                  redémarrez l&apos;API.
                </div>
              )}

              <p className="text-soft small mb-0">
                Les résultats sont des <strong>indications d&apos;aide à la décision</strong>, pas des prédictions
                certaines. Croisez toujours avec vos visites terrain et la réglementation.
              </p>
            </div>
          </PreviewCard>

          {result && (
            <>
              <Row className="g-gs mt-3">
                <Col size="12">
                  <PreviewCard className="card-full">
                    <div className="card-inner">
                      <h6 className="title mb-3">
                        <Icon name="file-text" className="me-2" />
                        Synthèse
                      </h6>
                      <p className="text-dark" style={{ whiteSpace: "pre-wrap" }}>
                        {result.summary || "—"}
                      </p>
                      {result.context && (
                        <p className="text-soft small mb-0 mt-2">
                          Données analysées : {result.context.observations_sample_size} observation(s) à risque,{" "}
                          {result.context.risk_zones_sample_size} zone(s) active(s)
                          {result.raw_model ? ` · Modèle ${result.raw_model}` : ""}
                        </p>
                      )}
                    </div>
                  </PreviewCard>
                </Col>
              </Row>

              <Row className="g-gs mt-2">
                <Col lg="6">
                  <PreviewCard className="card-full h-100">
                    <div className="card-inner">
                      <h6 className="title mb-3">
                        <Icon name="alert-circle" className="me-2 text-danger" />
                        Anticiper les accidents
                      </h6>
                      {Array.isArray(result.accident_risks) && result.accident_risks.length > 0 ? (
                        <ul className="list list-checked gy-2">
                          {result.accident_risks.map((r, i) => (
                            <li key={i} className="mb-3">
                              <div className="fw-medium">{r.title || `Risque ${i + 1}`}</div>
                              <p className="text-soft small mb-1">{r.rationale}</p>
                              {r.priority && (
                                <Badge color={priorityBadge(r.priority)} className="text-uppercase">
                                  {r.priority}
                                </Badge>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-soft">Aucun scénario proposé.</span>
                      )}
                    </div>
                  </PreviewCard>
                </Col>
                <Col lg="6">
                  <PreviewCard className="card-full h-100">
                    <div className="card-inner">
                      <h6 className="title mb-3">
                        <Icon name="map-pin" className="me-2 text-warning" />
                        Zones & situations dangereuses
                      </h6>
                      {Array.isArray(result.dangerous_zones) && result.dangerous_zones.length > 0 ? (
                        <ul className="list list-checked gy-2">
                          {result.dangerous_zones.map((z, i) => (
                            <li key={i} className="mb-3">
                              <div className="fw-medium">{z.label || `Zone ${i + 1}`}</div>
                              <p className="text-soft small mb-1">{z.rationale}</p>
                              {z.actions && (
                                <p className="small mb-0">
                                  <strong>Pistes d&apos;action :</strong> {z.actions}
                                </p>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-soft">Aucune zone identifiée.</span>
                      )}
                    </div>
                  </PreviewCard>
                </Col>
              </Row>
            </>
          )}
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default PredictiveAiPage;
